import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { DropCard } from '../components/DropCard';
import { dropsByChannel, type Channel, type Drop } from '../data/mock';
import { fetchChannel } from '../lib/bff';
import { formatCount } from '../lib/format';

/**
 * Channel page — channel hero + stats, then the work: curator-picked Featured,
 * the full chronological Latest grid, and active Drops. Empty sections are
 * omitted. See BRAND.md ▸ Page Templates ▸ Channel Page.
 */
export function ChannelPage() {
  const { slug } = useParams();
  const [channel, setChannel] = useState<Channel | null | undefined>(undefined);

  useEffect(() => {
    if (!slug) {
      setChannel(null);
      return;
    }
    let active = true;
    setChannel(undefined);
    fetchChannel(slug).then((res) => {
      if (active) setChannel(res);
    });
    return () => {
      active = false;
    };
  }, [slug]);

  if (channel === undefined) {
    return <main className="min-h-[100dvh] bg-provny-white dark:bg-provny-ink" />;
  }

  if (!channel) {
    return (
      <main className="mx-auto max-w-7xl px-6 py-24 text-center">
        <h1 className="font-heading text-3xl font-bold tracking-tight">Channel not found</h1>
        <p className="mt-3 text-provny-muted">This channel may have moved or never existed.</p>
        <Link to="/explore" className="mt-6 inline-block font-semibold text-provny-cyber-dim hover:underline dark:text-provny-cyber">
          ← Back to Explore
        </Link>
      </main>
    );
  }

  const drops = dropsByChannel(channel.slug);
  const featured = drops.filter((d) => d.featured);
  const active = drops.filter((d) => d.edition.sold < d.edition.total);

  return (
    <main className="min-h-[100dvh] bg-provny-white text-provny-black dark:bg-provny-ink dark:text-provny-white">
      {/* Channel hero */}
      <div className="relative aspect-[16/6] w-full overflow-hidden">
        <img src={channel.image} alt={channel.name} className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
        <div className="absolute inset-x-0 bottom-0">
          <div className="mx-auto max-w-7xl px-6 pb-6 text-white sm:pb-8">
            <h1 className="font-heading text-4xl font-black tracking-tight sm:text-5xl">{channel.name}</h1>
            <p className="mt-1.5 max-w-2xl text-white/80">{channel.tagline}</p>
            <div className="mt-4 flex flex-wrap items-center gap-4">
              <p className="font-mono text-sm text-white/70">
                {formatCount(channel.followers)} followers · {formatCount(channel.originals)} originals · {formatCount(channel.dropsCount)} drops
              </p>
              <button className="rounded-xl bg-provny-cyber px-4 py-2 text-sm font-semibold text-provny-ink transition hover:bg-provny-cyber/90">
                Follow
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-6 pb-20">
        {featured.length > 0 && <Section label="Featured" drops={featured} />}
        {drops.length > 0 && <Section label="Latest" drops={drops} />}
        {active.length > 0 && <Section label="Drops" drops={active} />}
      </div>
    </main>
  );
}

/** A titled grid of drop cards. */
function Section({ label, drops }: { label: string; drops: Drop[] }) {
  return (
    <section className="mt-12">
      <h2 className="mb-4 font-heading text-xl font-bold tracking-tight">{label}</h2>
      <div className="grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-4">
        {drops.map((drop) => (
          <DropCard key={drop.id} drop={drop} />
        ))}
      </div>
    </section>
  );
}
